import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Loader2, RefreshCw } from "lucide-react";
import { zamanAsimiDashboard } from "../lib/api";

function TierList({ title, items, color }) {
    if (!items?.length) return null;
    return (
        <div className="mb-4">
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${color}`}>
                {title}
            </h4>
            <ul className="space-y-1.5">
                {items.slice(0, 8).map((d) => (
                    <li key={d.id}>
                        <Link
                            to={`/e/sigorta-dosyalari/${d.id}`}
                            className="flex items-center gap-2 rounded-md border border-white/10 bg-[#0B0F19]/50 px-3 py-2 text-sm hover:border-[#D4AF37]/40 transition"
                        >
                            <span className={`shrink-0 text-xs font-mono ${color}`}>
                                {d.gun}g
                            </span>
                            <span className="truncate text-slate-200">
                                {d.muvekkil || "—"} · {d.dosya_no}
                            </span>
                            <span className="ml-auto text-[10px] text-slate-500 truncate max-w-[80px]">
                                {d.alt_durum_label}
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function ZamanAsimiPanel({ compact = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await zamanAsimiDashboard();
            setData(d);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 120000);
        return () => clearInterval(t);
    }, [load]);

    if (loading && !data) {
        return (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
                <Loader2 className="h-4 w-4 animate-spin" /> Zaman aşımı taranıyor…
            </div>
        );
    }

    const oz = data?.ozet || {};

    return (
        <section
            className={`rounded-xl border border-white/10 bg-[#121A2F]/80 ${
                compact ? "p-4" : "p-5"
            }`}
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-[#D4AF37]" />
                    <h3 className="font-display text-lg text-white">Zaman Aşımı Takibi</h3>
                </div>
                <div className="flex items-center gap-2">
                    {data?.son_tarama && (
                        <span className="text-[10px] text-slate-500">
                            Son tarama{" "}
                            {new Date(data.son_tarama).toLocaleTimeString("tr-TR", {
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={load}
                        className="h-8 w-8 rounded-md border border-white/10 flex items-center justify-center text-slate-400 hover:text-white"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-center">
                    <div className="text-xl font-semibold text-rose-300">{oz.kirmizi_30 || 0}</div>
                    <div className="text-[10px] text-rose-400/80">30+ gün</div>
                </div>
                <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 px-3 py-2 text-center">
                    <div className="text-xl font-semibold text-orange-300">{oz.turuncu_21 || 0}</div>
                    <div className="text-[10px] text-orange-400/80">21+ gün</div>
                </div>
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-center">
                    <div className="text-xl font-semibold text-amber-300">{oz.sari_14 || 0}</div>
                    <div className="text-[10px] text-amber-400/80">14+ gün</div>
                </div>
            </div>
            <TierList title="🔴 30+ gün" items={data?.kirmizi_30} color="text-rose-400" />
            <TierList title="🟠 21+ gün" items={data?.turuncu_21} color="text-orange-400" />
            <TierList title="🟡 14+ gün" items={data?.sari_14} color="text-amber-400" />
            {!compact && (
                <Link
                    to="/yeni/zaman-asimi"
                    className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline mt-2"
                >
                    Tüm listeyi gör →
                </Link>
            )}
        </section>
    );
}
